import React, { useState } from "react";
import HeaderPurchase from "../../components/HeaderPurchase";
import {
  Content,
  ViewTextInput,
  ViewButton,
  ViewText,
  ViewHelper,
  ViewTitle,
  VerticalScroll,
} from "./style";
import THEME from "../../config/theme";
import TextInput from "../../components/TextInput";
import Button from "../../components/Button";
import { createUserWithEmailAndPassword } from "firebase/auth";
import { setDoc, doc, serverTimestamp } from "firebase/firestore";
import { auth, firestore } from "../../services/firebase";
import { AlertBox } from "../../components/AlertBox";
import { HelperText, Provider, RadioButton } from "react-native-paper";
import { Title } from "../../config/theme/globalStyles";
import { SafeAreaView, View, Text, StyleSheet } from "react-native";
import { createCheckoutSession } from "../../services/stripe/createCheckoutSessionBlix";

export function BlixSignUp({ route }) {

  /**
   * SignUp page used in the subscription flow for Blix Growth or Premium plans.
   *
   * This page exists to:
   * 1. Create the Blix customer in Firebase Authentication.
   * 2. Create the user document in Firestore.
   * 3. Start a Stripe checkout session based on the selected plan.
   *
   * This flow ensures that:
   * - The user already exists in the system before payment and is automatically created with (customerId, subscriptionId, subscription_item_id, tax ID, and billing address).
   * - The subscription is properly linked to the user’s uid in Firestore.
   * - All Blix customer subscription data is stored in our Firestore database.
   * - The Stripe + Firebase extension can correctly associate the customer with the user.
   *
   * This page is NOT a regular sign-up page and is NOT used in client apps.
   * It is part of Blix’s own transactional plan acquisition flow.
   */

  const { productId } = route.params;
  const [paymentMethod, setPaymentMethod] = useState("card");
  const isCard = paymentMethod === "card";

  const [email, setEmail] = useState({ value: "", error: "" });
  const [loading, setLoading] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [title, setTitle] = useState(null);
  const [message, setMessege] = useState(null);

  const [legalName, setLegalName] = useState("");
  const [cnpj, setCnpj] = useState("");

  const fetchCnpjData = async (value) => {
    const cleanCnpj = value.replace(/\D/g, "");

    if (cleanCnpj.length !== 14) return;

    try {
      const response = await fetch(
        `https://brasilapi.com.br/api/cnpj/v1/${cleanCnpj}`
      );

      const data = await response.json();

      setLegalName(data.razao_social);
    } catch (error) {
      console.log("Erro ao buscar CNPJ", error);
    }
  };

  const showAlert = (title, message) => {
    setVisibleAlert(true);
    setTitle(title);
    setMessege(message);
  };

  const hideAlert = (status) => {
    setVisibleAlert(status);
  };

  const createUserAndSession = (userId) => {
    const usersCollectionRef = doc(firestore, "users", userId);
    const createUser = async () => {
      const userData = {
        Email: email.value,
        createdAt: serverTimestamp(),
      };

      userData.billing = {
        legal_name: legalName,
        tax_id: cnpj.replace(/\D/g, ""),
      };

      await setDoc(usersCollectionRef, userData);
    };

    createUser()
      .then(() => {
        console.log("productId is", productId, "paymentMethod is", paymentMethod)
        createCheckoutSession(userId, productId, paymentMethod)
      })
      .catch((error) => {
        console.log(error);
        setLoading(false);
      });
  };

  const onSignUpPressed = () => {
    if (
      email.value
    ) {
      setLoading(true);
      createUserWithEmailAndPassword(auth, email.value, '123456')
        .then((currentUser) => {
          // Lida com o resultado, se necessário
          createUserAndSession(currentUser.user.uid)
        })
        .catch((error) => {
          setLoading(false);
          console.error("erro:", error);
          switch (error.code) {
            case "auth/email-already-in-use":
              showAlert(
                "Erro:",
                "Este email já está cadastrado. Por favor, escolha outro."
              );
              break;
            case "auth/invalid-email":
              showAlert("Erro:", "Email inválido.");
              break;
            case "auth/operation-not-allowed":
              showAlert("Erro:", "Problemas ao cadastrar o usuário.");
              break;
          }
        });
    }
  };

  return (
    <SafeAreaView style={{ flexGrow: 1, backgroundColor: THEME.COLORS.BACKGROUND_ABOUT }}>
      <Provider>
        <VerticalScroll>
          <HeaderPurchase signUp={true} />
          <ViewTitle>
            <Title
              fontSize={THEME.FONTSIZE.MEDIUM}
              textAlign="left"
              padding="1rem 0rem 0rem 1rem"
            >
              Criar uma conta
            </Title>
          </ViewTitle>
          <Content>
            <ViewTextInput>
              <ViewText>
                <TextInput
                  label="Email de faturamento"
                  placeholder="Digite seu email de faturamento"
                  returnKeyType="next"
                  value={email.value}
                  onChangeText={(text) => setEmail({ value: text, error: "" })}
                  error={!!email.error}
                  autoCapitalize="none"
                  autoCompleteType="email"
                  textContentType="emailAddress"
                  keyboardType="email-address"
                />
              </ViewText>
              <ViewHelper>
                <HelperText type="error" visible={email.error}>
                  {email.error}
                </HelperText>
              </ViewHelper>
            </ViewTextInput>
            <View style={styles.paymentMethodContainer}>
              <Text style={styles.paymentMethodLabel}>Método de pagamento</Text>
              <RadioButton.Group onValueChange={setPaymentMethod} value={paymentMethod}>
                <View style={styles.radioRow}>
                  <RadioButton.Item
                    label="Cartão de crédito"
                    value="card"
                    color={THEME.COLORS.PRIMARY_800}
                    labelStyle={styles.radioLabel}
                  />
                  <RadioButton.Item
                    label="Boleto bancário"
                    value="boleto"
                    color={THEME.COLORS.PRIMARY_800}
                    labelStyle={styles.radioLabel}
                  />
                </View>
              </RadioButton.Group>
            </View>
            <>
              <ViewTextInput>
                <ViewText>
                  <TextInput
                    label="CNPJ"
                    placeholder="Digite o CNPJ"
                    value={cnpj}
                    onChangeText={(text) => {
                      setCnpj(text);
                      fetchCnpjData(text);
                    }}
                    keyboardType="numeric"
                  />
                </ViewText>
              </ViewTextInput>
              <ViewTextInput>
                <ViewText>
                  <TextInput
                    label="Razão Social"
                    value={legalName}
                    onChangeText={(text) => setLegalName(text)}
                  />
                </ViewText>
              </ViewTextInput>
            </>
          </Content>
          <ViewButton>
            <Button
              title={"Ir para pagamento"}
              isLoading={loading}
              onPress={onSignUpPressed}
            ></Button>
          </ViewButton>
          {visibleAlert && (
            <AlertBox
              title={title}
              message={message}
              visible={visibleAlert}
              onClose={hideAlert}
            ></AlertBox>
          )}
        </VerticalScroll>
      </Provider>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  paymentMethodContainer: {
    paddingVertical: 8,
  },
  paymentMethodLabel: {
    fontSize: 14,
    color: THEME.COLORS.TEXT_ABOUT,
    fontWeight: "600",
    marginBottom: 4,
  },
  radioRow: {
    flexDirection: "row",
    alignItems: "center",
  },
  radioLabel: {
    fontSize: 14,
    color: THEME.COLORS.TEXT_ABOUT,
  },
});